import { useRef, useState } from "react";
import {
  useDiaryStyleStatus,
  useDiaryStyleSamples,
  useDiaryStylePresets,
  useDiaryStyleNotices,
  useLearnDiaryStyle,
  useDeleteDiaryStyleSample,
} from "~/services/business/diary";

/** 파일이 이미지인지 (아니면 문서 취급) */
const isImageFile = (f: File) =>
  f.type.startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/i.test(f.name);

/**
 * AI 알림장 — 내 문체 스타일 등록/관리 패널.
 *  - 상태 카드(등록됨/미등록 + 학습일 + 샘플수)
 *  - 샘플 추가: 사진/문서(pdf·docx·한글) 업로드 + 예시 문구 붙여넣기 + 기존 알림장 취합
 *  - 추가 학습(APPEND) / 다시 학습(RESET)
 *  - 등록된 샘플 목록 + 삭제
 * 미등록이어도 시스템 프리셋으로 초안이 생성되므로, 등록은 "내 색깔 입히기".
 */
export function NotifyStylePanel() {
  const { data: status } = useDiaryStyleStatus();
  const { data: samples } = useDiaryStyleSamples();
  const { data: presets } = useDiaryStylePresets();
  const learn = useLearnDiaryStyle();
  const deleteSample = useDeleteDiaryStyleSample();

  const [files, setFiles] = useState<File[]>([]);
  const [text, setText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 기존 알림장 취합
  const [pickNotices, setPickNotices] = useState(false);
  const { data: notices } = useDiaryStyleNotices(pickNotices);
  const [selectedNotices, setSelectedNotices] = useState<Set<number>>(
    new Set(),
  );

  const registered = !!status?.registered;
  const busy = learn.isPending || deleteSample.isPending;

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    setFiles((prev) => [...prev, ...picked]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const toggleNotice = (seq: number) =>
    setSelectedNotices((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });

  const nothingToLearn =
    files.length === 0 && !text.trim() && selectedNotices.size === 0;

  const submit = (mode: "APPEND" | "RESET") => {
    if (nothingToLearn) return;
    const texts = text.trim() ? [text.trim()] : [];
    learn.mutate(
      { mode, files, texts, noticeSeqs: [...selectedNotices] },
      {
        onSuccess: () => {
          setFiles([]);
          setText("");
          setSelectedNotices(new Set());
        },
      },
    );
  };

  const fmtDate = (s?: string | null) =>
    s ? s.slice(0, 10).replace(/-/g, ".") : "-";

  return (
    <div className="flex flex-col gap-4 pb-24">
      {/* 상태 카드 */}
      <div className="bg-white border-2 border-[#E9E9E9] rounded-xl p-4">
        {registered ? (
          <div>
            <div className="flex items-center gap-2 text-[15px] font-medium">
              <span className="text-[#1D9E75]">●</span> 내 스타일 등록됨
              {!status?.styleReady && (
                <span className="text-[12px] text-[#BA7517]">
                  · 학습 처리 중
                </span>
              )}
            </div>
            <div className="mt-1 text-[12px] text-[#888780]">
              {fmtDate(status?.dtLearned)} 학습 · 샘플{" "}
              {status?.sampleCount ?? 0}개
            </div>
          </div>
        ) : (
          <div>
            <div className="text-[15px] font-medium text-[#D85A30]">
              아직 내 스타일이 없어요
            </div>
            <div className="mt-1 text-[13px] leading-relaxed text-[#5F5E5A]">
              내가 쓴 알림장을 사진·문서로 올리거나, 아래에서 기존 알림장을
              골라주면 AI가 내 문체로 초안을 써줘요. 등록 전에는 기본 스타일
              {presets && presets.length > 0 ? `(${presets[0].name} 등)` : ""}로
              생성됩니다.
            </div>
          </div>
        )}
      </div>

      {/* 샘플 추가 */}
      <div className="bg-white border-2 border-[#E9E9E9] rounded-xl p-4">
        <div className="text-[14px] font-medium mb-2">샘플 추가</div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf,.doc,.docx,.hwp,.hwpx,.txt"
          multiple
          className="hidden"
          onChange={onPickFiles}
        />
        <button
          type="button"
          className="w-full border border-[#E9E9E9] rounded-lg py-3 text-[14px] text-[#5F5E5A]"
          onClick={() => fileInputRef.current?.click()}
        >
          + 사진·문서 불러오기 (pdf·워드·한글)
        </button>

        {files.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-2">
            {files.map((f, idx) => (
              <div
                key={idx}
                className="relative aspect-square rounded-lg overflow-hidden border border-[#E9E9E9]"
              >
                {isImageFile(f) ? (
                  <img
                    src={URL.createObjectURL(f)}
                    alt="샘플"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[#F7F6F2] flex flex-col items-center justify-center px-1 text-center">
                    <span className="text-[18px]">📄</span>
                    <span className="mt-0.5 text-[9px] leading-tight text-[#888780] break-all line-clamp-2">
                      {f.name}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/50 text-white text-[12px] leading-5"
                  onClick={() => removeFile(idx)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <textarea
          className="w-full border border-[#E9E9E9] rounded-lg p-3 mt-3 text-[14px] resize-none"
          rows={4}
          placeholder="또는 알림장 예시 문구를 직접 붙여넣어도 돼요."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        {/* 기존 알림장에서 가져오기 */}
        <div className="mt-3 border-t border-[#F1EFE8] pt-3">
          <button
            type="button"
            className="flex w-full items-center justify-between text-[13px] text-[#5F5E5A]"
            onClick={() => setPickNotices((v) => !v)}
          >
            <span>
              📒 기존 알림장에서 가져오기
              {selectedNotices.size > 0 && (
                <span className="ml-1 text-[#FF9934]">
                  ({selectedNotices.size})
                </span>
              )}
            </span>
            <span className="text-[#B7B5AC]">{pickNotices ? "▲" : "▼"}</span>
          </button>

          {pickNotices && (
            <div className="mt-2 max-h-56 overflow-y-auto flex flex-col gap-1.5">
              {(notices ?? []).map((n) => {
                const on = selectedNotices.has(n.noticeSeq);
                return (
                  <button
                    key={n.noticeSeq}
                    type="button"
                    onClick={() => toggleNotice(n.noticeSeq)}
                    className={`flex items-start gap-2 rounded-lg border p-2 text-left ${
                      on
                        ? "border-[#FF9934] bg-[#FFF7E6]"
                        : "border-[#F1EFE8] bg-white"
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] ${
                        on
                          ? "border-[#FF9934] bg-[#FF9934] text-white"
                          : "border-[#D3D1C7] text-transparent"
                      }`}
                    >
                      ✓
                    </span>
                    <div className="min-w-0">
                      <div className="text-[12px] font-medium text-[#3D3B36]">
                        {n.petName || "알림장"}
                        <span className="ml-1 font-normal text-[#B7B5AC]">
                          {fmtDate(n.dtRegist)}
                        </span>
                      </div>
                      <div className="text-[12px] text-[#888780] line-clamp-2">
                        {n.preview || ""}
                      </div>
                    </div>
                  </button>
                );
              })}
              {notices && notices.length === 0 && (
                <div className="py-4 text-center text-[12px] text-[#888780]">
                  가져올 기존 알림장이 없어요.
                </div>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          disabled={busy || nothingToLearn}
          className="w-full mt-3 rounded-lg py-3 text-[14px] font-medium text-white bg-[#FF9934] disabled:opacity-40"
          onClick={() => submit("APPEND")}
        >
          {registered ? "추가 학습하기" : "스타일 등록하기"}
        </button>
        {registered && (
          <button
            type="button"
            disabled={busy || nothingToLearn}
            className="w-full mt-2 rounded-lg py-2.5 text-[13px] text-[#5F5E5A] border border-[#E9E9E9] disabled:opacity-40"
            onClick={() => submit("RESET")}
          >
            기존 샘플 지우고 다시 학습
          </button>
        )}
      </div>

      {/* 등록된 샘플 */}
      {samples && samples.length > 0 && (
        <div className="bg-white border-2 border-[#E9E9E9] rounded-xl p-4">
          <div className="text-[14px] font-medium mb-2">
            등록된 샘플 {samples.length}개
          </div>
          <div className="flex flex-col gap-2">
            {samples.map((s) => (
              <div
                key={s.sampleSeq}
                className="flex items-center gap-3 border border-[#F1EFE8] rounded-lg p-2"
              >
                {s.kind === "IMAGE" && s.fileUrl ? (
                  <img
                    src={s.fileUrl}
                    alt="샘플"
                    className="w-12 h-12 rounded object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-[#F1EFE8] flex items-center justify-center text-[11px] text-[#888780]">
                    {s.kind === "FILE" ? "📄" : "텍스트"}
                  </div>
                )}
                <div className="flex-1 min-w-0 text-[13px] text-[#5F5E5A] truncate">
                  {s.kind === "TEXT"
                    ? s.textPreview || "텍스트 샘플"
                    : s.kind === "FILE"
                      ? "문서 샘플"
                      : "사진 샘플"}
                </div>
                <button
                  type="button"
                  disabled={busy}
                  className="text-[12px] text-[#D4537E] px-2 disabled:opacity-40"
                  onClick={() => deleteSample.mutate(s.sampleSeq)}
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
